import classNames from 'classnames';
import * as customPropTypes from 'custom-prop-types';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';

import { LanguageProvider, useLanguageContext } from '@/design-system/contexts/LanguageContext';
import { useMarketo } from '@/hooks/useMarketo';

import styles from './MarketoForm.module.scss';

const MarketoForm = (props) => {
    const {
        baseUrl,
        munchkinId,
        formId,
        spanishId,
        successFragment,
        spanishDebugMode,
        formType,
        analytics,
        testMode,
    } = props;
    const { isSpanish } = useLanguageContext();
    const form_id = (isSpanish || spanishDebugMode) && spanishId ? spanishId : formId;
    const [submitSuccess, setSubmitSuccess] = React.useState(false);
    const formWapperRef = useRef(null);

    /**
     * Scrolls the form wrapper into view if it is not fully visible in the viewport.
     * Intended to be called after a successful form submission to ensure the user sees the result.
     */
    const scrollToFormIfNotVisible = () => {
        const el = formWapperRef?.current;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const viewportTop = 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const isVisible = rect.top >= viewportTop && rect.bottom <= viewportHeight;

        if (!isVisible) {
            el.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    };

    useMarketo({
        baseUrl,
        munchkinId,
        formId: form_id,
        callbacks: {
            onSuccess: (values, followUpUrl) => {
                setSubmitSuccess(true);
            },
            onSuccessScrollToElement: scrollToFormIfNotVisible,
        },
        analytics,
        testMode,
        formType,
        formWapperRef,
    });
    return (
        <LanguageProvider>
            <div
                ref={formWapperRef}
                className={classNames(
                    styles[`marketo-form`],
                    formType && styles[`marketo-form--${formType}`],
                )}
            >
                {submitSuccess ? (
                    successFragment ? (
                        <div
                            role="alert"
                            aria-atomic="true"
                            className={styles[`marketo-form__success`]}
                        >
                            {successFragment}
                        </div>
                    ) : (
                        <></>
                    )
                ) : (
                    <form
                        id={`mktoForm_${form_id}`}
                        data-form-id={form_id}
                        data-form-instance={Date.now()}
                        data-form-type={formType}
                    ></form>
                )}
            </div>
        </LanguageProvider>
    );
};

MarketoForm.propTypes = {
    /**
     * URL to the Marketo server instance
     */
    baseUrl: PropTypes.string,
    /**
     * Munchkin ID
     */
    munchkinId: PropTypes.string,
    /**
     * marketo Form Id
     */
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * spanish Form Id
     */
    spanishFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * successFragment
     */
    successFragment: PropTypes.node,
    /**
     * analytics
     */
    analytics: customPropTypes.analyticsPropType,
};

export default MarketoForm;
